import { Button } from "../../ui/components/Button";
import { Select, SelectItem } from "../../ui/components/Select";
import { NumberField } from "../../ui/components/NumberField";
import { Dialog, DialogTrigger } from "../../ui/components/Dialog";
import { Download } from "lucide-react";
import { Modal } from "react-aria-components";
import { useState, useRef } from "react";
import type {
  EditorConfig,
  EditorFeature,
  EditorValues,
  ExportSettings,
  ExportFormat,
  TonalCurvePoint,
  TonalCurveValues,
  ColorGradingCircle,
  ColorGradingValues,
} from "../types";
import { ColorGradingEditor } from "./ColorGradingEditor";
import { EditorSection } from "./EditorSection";
import { EditorSlider } from "./EditorSlider";
import { TonalCurveEditor } from "./TonalCurveEditor";
import { luminaService } from "../services/luminaService";
import { useDebounce } from "../hooks/useDebounce";

interface EditorSidebarProps {
  config: EditorConfig;
  values: EditorValues;
  onChange: (values: Partial<EditorValues>) => void;
  onExport?: (settings: ExportSettings) => Promise<void>;
  onPreviewUpdate?: (url: string) => void;
}

interface SectionState {
  exportOptions: boolean;
  globalLight: boolean;
  regionalLight: boolean;
  color: boolean;
  tonalCurve: boolean;
  colorGrading: boolean;
}

export function EditorSidebar({
  config,
  values,
  onChange,
  onExport,
  onPreviewUpdate,
}: EditorSidebarProps) {
  const [collapsedSections, setCollapsedSections] = useState<SectionState>({
    exportOptions: true,
    globalLight: true,
    regionalLight: true,
    color: true,
    tonalCurve: true,
    colorGrading: true,
  });

  // Operation tracking for cancellation awareness
  const currentPreviewOperation = useRef(0);

  // Debounced preview functions with improved performance
  const debouncedPreviewExposure = useDebounce((value: number) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewExposure(value)
      .then((previewUrl) => {
        // Only update UI if this operation wasn't superseded
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          // Clean up blob URL from cancelled operation
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview exposure failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewBrightness = useDebounce((value: number) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewBrightness(value)
      .then((previewUrl) => {
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview brightness failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewContrast = useDebounce((value: number) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewContrast(value)
      .then((previewUrl) => {
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview contrast failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewSaturation = useDebounce((value: number) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewSaturation(value)
      .then((previewUrl) => {
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview saturation failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewTemperature = useDebounce((temperature: number, tint: number) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewTemperature(temperature, tint)
      .then((previewUrl) => {
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview temperature failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewHighlightsShadows = useDebounce(
    (highlights: number, shadows: number, midtones: number) => {
      const operationId = ++currentPreviewOperation.current;

      luminaService
        .previewHighlightsShadows(highlights, shadows, midtones)
        .then((previewUrl) => {
          if (operationId === currentPreviewOperation.current) {
            onPreviewUpdate?.(previewUrl);
          } else {
            URL.revokeObjectURL(previewUrl);
          }
        })
        .catch((error) => {
          if (operationId === currentPreviewOperation.current) {
            console.error("Preview highlights/shadows failed:", error);
          }
        });
    },
    150
  );

  const debouncedPreviewTonalCurve = useDebounce((points: TonalCurvePoint[]) => {
    const operationId = ++currentPreviewOperation.current;

    luminaService
      .previewTonalCurve(points)
      .then((previewUrl) => {
        if (operationId === currentPreviewOperation.current) {
          onPreviewUpdate?.(previewUrl);
        } else {
          URL.revokeObjectURL(previewUrl);
        }
      })
      .catch((error) => {
        if (operationId === currentPreviewOperation.current) {
          console.error("Preview tonal curve failed:", error);
        }
      });
  }, 150);

  const debouncedPreviewColorGrading = useDebounce(
    (colorGrading: {
      shadows: ColorGradingCircle;
      midtones: ColorGradingCircle;
      highlights: ColorGradingCircle;
    }) => {
      const operationId = ++currentPreviewOperation.current;

      luminaService
        .previewColorGrading(colorGrading)
        .then((previewUrl) => {
          if (operationId === currentPreviewOperation.current) {
            onPreviewUpdate?.(previewUrl);
          } else {
            URL.revokeObjectURL(previewUrl);
          }
        })
        .catch((error) => {
          if (operationId === currentPreviewOperation.current) {
            console.error("Preview color grading failed:", error);
          }
        });
    },
    150
  );

  // Preview handlers (called during slider drag)
  const handlePreviewExposure = (value: number) => {
    // Update local state immediately for responsive UI
    onChange({ globalLight: { ...values.globalLight, exposure: value } });

    // Debounced async preview update
    debouncedPreviewExposure(value);
  };

  const handlePreviewBrightness = (value: number) => {
    onChange({ globalLight: { ...values.globalLight, brightness: value } });
    debouncedPreviewBrightness(value);
  };

  const handlePreviewContrast = (value: number) => {
    onChange({ globalLight: { ...values.globalLight, contrast: value } });
    debouncedPreviewContrast(value);
  };

  const handlePreviewSaturation = (value: number) => {
    onChange({ color: { ...values.color, saturation: value } });
    debouncedPreviewSaturation(value);
  };

  const handlePreviewTemperature = (temperature: number) => {
    const newColorValues = { ...values.color, temperature };
    onChange({ color: newColorValues });
    debouncedPreviewTemperature(temperature, newColorValues.tint);
  };

  const handlePreviewTint = (tint: number) => {
    const newColorValues = { ...values.color, tint };
    onChange({ color: newColorValues });
    debouncedPreviewTemperature(newColorValues.temperature, tint);
  };

  const handlePreviewShadows = (shadowsValue: number) => {
    const newRegionalValues = { ...values.regionalLight, shadows: shadowsValue };
    onChange({ regionalLight: newRegionalValues });
    debouncedPreviewHighlightsShadows(
      newRegionalValues.highlights,
      shadowsValue,
      newRegionalValues.midtones
    );
  };

  const handlePreviewHighlights = (highlightsValue: number) => {
    const newRegionalValues = { ...values.regionalLight, highlights: highlightsValue };
    onChange({ regionalLight: newRegionalValues });
    debouncedPreviewHighlightsShadows(
      highlightsValue,
      newRegionalValues.shadows,
      newRegionalValues.midtones
    );
  };

  const handlePreviewMidtones = (midtonesValue: number) => {
    const newRegionalValues = { ...values.regionalLight, midtones: midtonesValue };
    onChange({ regionalLight: newRegionalValues });
    debouncedPreviewHighlightsShadows(
      newRegionalValues.highlights,
      newRegionalValues.shadows,
      midtonesValue
    );
  };

  const handlePreviewTonalCurve = (points: TonalCurvePoint[]) => {
    debouncedPreviewTonalCurve(points);
  };

  const handlePreviewColorGrading = (colorGrading: {
    shadows: ColorGradingCircle;
    midtones: ColorGradingCircle;
    highlights: ColorGradingCircle;
  }) => {
    debouncedPreviewColorGrading(colorGrading);
  };

  // Helper function to handle apply operations with preview refresh
  const handleApplyWithPreview = async (applyFn: () => Promise<string>, operationName: string) => {
    try {
      // Invalidate pending previews since SDK will cancel them anyway
      currentPreviewOperation.current++;

      // Apply operation now returns preview URL directly - no redundant call needed
      const previewUrl = await applyFn();
      onPreviewUpdate?.(previewUrl);
    } catch (error) {
      console.error(`Apply ${operationName} failed:`, error);
    }
  };

  // Apply handlers (called when slider is released)
  const handleApplyExposure = async (value: number) => {
    await handleApplyWithPreview(() => luminaService.applyExposure(value), "exposure");
  };

  const handleApplyBrightness = async (value: number) => {
    await handleApplyWithPreview(() => luminaService.applyBrightness(value), "brightness");
  };

  const handleApplyContrast = async (value: number) => {
    await handleApplyWithPreview(() => luminaService.applyContrast(value), "contrast");
  };

  const handleApplySaturation = async (value: number) => {
    await handleApplyWithPreview(() => luminaService.applySaturation(value), "saturation");
  };

  const handleApplyTemperature = async (temperature: number) => {
    await handleApplyWithPreview(
      () => luminaService.applyTemperature(temperature, values.color.tint),
      "temperature"
    );
  };

  const handleApplyTint = async (tint: number) => {
    await handleApplyWithPreview(
      () => luminaService.applyTemperature(values.color.temperature, tint),
      "tint"
    );
  };

  const handleApplyShadows = async (shadowsValue: number) => {
    await handleApplyWithPreview(
      () =>
        luminaService.applyHighlightsShadows(
          values.regionalLight.highlights,
          shadowsValue,
          values.regionalLight.midtones
        ),
      "shadows"
    );
  };

  const handleApplyHighlights = async (highlightsValue: number) => {
    await handleApplyWithPreview(
      () =>
        luminaService.applyHighlightsShadows(
          highlightsValue,
          values.regionalLight.shadows,
          values.regionalLight.midtones
        ),
      "highlights"
    );
  };

  const handleApplyMidtones = async (midtonesValue: number) => {
    await handleApplyWithPreview(
      () =>
        luminaService.applyHighlightsShadows(
          values.regionalLight.highlights,
          values.regionalLight.shadows,
          midtonesValue
        ),
      "midtones"
    );
  };

  const handleApplyTonalCurve = async (points: TonalCurvePoint[]) => {
    await handleApplyWithPreview(() => luminaService.applyTonalCurve(points), "tonal curve");
  };

  const handleApplyColorGrading = async (colorGrading: {
    shadows: ColorGradingCircle;
    midtones: ColorGradingCircle;
    highlights: ColorGradingCircle;
  }) => {
    await handleApplyWithPreview(
      () => luminaService.applyColorGrading(colorGrading),
      "color grading"
    );
  };

  const [exportSettings, setExportSettings] = useState<ExportSettings>({
    quality: 100,
    format: luminaService.getDefaultExportFormat(),
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const toggleSection = (section: keyof SectionState) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isFeatureEnabled = (feature: string) => {
    return config.features.includes(feature as EditorFeature);
  };

  // Check if feature is disabled for free users
  const isAdvancedFeature = (feature: string) => {
    const advancedFeatures = ["tonal-curve", "color-grading"];
    return advancedFeatures.includes(feature) && !config.allowAdvancedFeatures;
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Scrollable Content */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-0">
          {/* Global Light Section */}
          {isFeatureEnabled("global-light") && (
            <EditorSection
              title="Global Light"
              description="Exposure adjusts light in stops, and Brightness shifts midtones. Contrast separates lights from darks."
              isCollapsed={collapsedSections.globalLight}
              onToggleCollapse={() => toggleSection("globalLight")}
            >
              <div className="space-y-4">
                <EditorSlider
                  label="Exposure"
                  value={values.globalLight.exposure}
                  min={-5}
                  max={5}
                  step={0.01}
                  onChange={handlePreviewExposure}
                  onChangeEnd={handleApplyExposure}
                  colorVariant="exposure"
                />
                <EditorSlider
                  label="Brightness"
                  value={values.globalLight.brightness}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewBrightness}
                  onChangeEnd={handleApplyBrightness}
                  colorVariant="brightness"
                />
                <EditorSlider
                  label="Contrast"
                  value={values.globalLight.contrast}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewContrast}
                  onChangeEnd={handleApplyContrast}
                  colorVariant="contrast"
                />
              </div>
            </EditorSection>
          )}

          {/* Regional Light Section */}
          {isFeatureEnabled("regional-light") && (
            <EditorSection
              title="Regional Light"
              description="Shadows brightens or darkens dark tones, and Highlights recovers or pushes bright areas. Midtones adjusts the middle range."
              isCollapsed={collapsedSections.regionalLight}
              onToggleCollapse={() => toggleSection("regionalLight")}
            >
              <div className="space-y-4">
                <EditorSlider
                  label="Shadows"
                  value={values.regionalLight.shadows}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewShadows}
                  onChangeEnd={handleApplyShadows}
                  colorVariant="shadows"
                />
                <EditorSlider
                  label="Highlights"
                  value={values.regionalLight.highlights}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewHighlights}
                  onChangeEnd={handleApplyHighlights}
                  colorVariant="highlights"
                />
                <EditorSlider
                  label="Midtones"
                  value={values.regionalLight.midtones}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewMidtones}
                  onChangeEnd={handleApplyMidtones}
                  colorVariant="midtones"
                />
              </div>
            </EditorSection>
          )}

          {/* Color Section */}
          {isFeatureEnabled("color") && (
            <EditorSection
              title="Color"
              description="Saturation controls color intensity. Temperature shifts between cool and warm, and Tint shifts between green and magenta."
              isCollapsed={collapsedSections.color}
              onToggleCollapse={() => toggleSection("color")}
            >
              <div className="space-y-4">
                <EditorSlider
                  label="Saturation"
                  value={values.color.saturation}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewSaturation}
                  onChangeEnd={handleApplySaturation}
                  colorVariant="saturation"
                />
                <EditorSlider
                  label="Temperature"
                  value={values.color.temperature}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewTemperature}
                  onChangeEnd={handleApplyTemperature}
                  colorVariant="temperature"
                />
                <EditorSlider
                  label="Tint"
                  value={values.color.tint}
                  min={-100}
                  max={100}
                  step={1}
                  onChange={handlePreviewTint}
                  onChangeEnd={handleApplyTint}
                  colorVariant="tint"
                />
              </div>
            </EditorSection>
          )}

          {/* Tonal Curve Section */}
          {isFeatureEnabled("tonal-curve") && (
            <EditorSection
              title="Tonal Curve"
              description="Drag points on the curve to remap tonal values. S-curves add contrast, and inverse curves produce stylized effects."
              isCollapsed={collapsedSections.tonalCurve}
              onToggleCollapse={() => toggleSection("tonalCurve")}
              disabled={isAdvancedFeature("tonal-curve")}
            >
              <TonalCurveEditor
                value={values.tonalCurve}
                onChange={(tonalCurve) => {
                  onChange({ tonalCurve });
                  handlePreviewTonalCurve(tonalCurve.points);
                }}
                onChangeEnd={(tonalCurve: TonalCurveValues) => {
                  handleApplyTonalCurve(tonalCurve.points);
                }}
                disabled={isAdvancedFeature("tonal-curve")}
              />
              {isAdvancedFeature("tonal-curve") && (
                <div className="mt-2 rounded bg-muted/50 p-2 text-muted-foreground text-xs">
                  Sign up to unlock tonal curve editing
                </div>
              )}
            </EditorSection>
          )}

          {/* Color Grading Section */}
          {isFeatureEnabled("color-grading") && (
            <EditorSection
              title="Color Grading"
              description="Tint shadows, midtones, and highlights independently. Use the Blend slider to control how the tonal ranges overlap."
              isCollapsed={collapsedSections.colorGrading}
              onToggleCollapse={() => toggleSection("colorGrading")}
              disabled={isAdvancedFeature("color-grading")}
            >
              <ColorGradingEditor
                value={values.colorGrading}
                onChange={(colorGrading) => {
                  onChange({ colorGrading });
                  handlePreviewColorGrading(colorGrading);
                }}
                onChangeEnd={(colorGrading: ColorGradingValues) => {
                  handleApplyColorGrading(colorGrading);
                }}
                disabled={isAdvancedFeature("color-grading")}
              />
              {isAdvancedFeature("color-grading") && (
                <div className="mt-2 rounded bg-muted/50 p-2 text-muted-foreground text-xs">
                  Sign up to unlock color grading
                </div>
              )}
            </EditorSection>
          )}
        </div>
      </div>

      {/* Fixed Export section - Hidden on mobile (< md breakpoint) */}
      <div className="hidden md:block border-t bg-background p-4 flex-shrink-0">
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-sm mb-1">Export Options</h3>
            <p className="text-muted-foreground text-xs mb-3">
              Choose the format and quality, then export your edited image.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Format"
              selectedKey={exportSettings.format}
              onSelectionChange={(format) =>
                setExportSettings((prev) => ({ ...prev, format: format as ExportFormat }))
              }
            >
              <SelectItem id="jpeg">JPEG</SelectItem>
              <SelectItem id="png">PNG</SelectItem>
              <SelectItem id="webp">WebP</SelectItem>
            </Select>

            <NumberField
              label="Quality"
              value={exportSettings.quality}
              minValue={50}
              maxValue={100}
              onChange={(quality) =>
                setExportSettings((prev) => ({ ...prev, quality: quality || 100 }))
              }
              placeholder="100"
            />
          </div>

          <div className="space-y-2">
            {config.allowExport && onExport && (
              <Button
                variant="outline"
                onPress={async () => {
                  setIsExporting(true);
                  setExportProgress(0);

                  try {
                    // Simulate progress updates
                    const progressInterval = setInterval(() => {
                      setExportProgress((prev) => {
                        if (prev >= 90) {
                          clearInterval(progressInterval);
                          return 90; // Stop at 90% until actual export completes
                        }
                        const increment = Math.random() * 15 + 5;
                        return Math.min(prev + increment, 90); // Cap at 90%
                      });
                    }, 100);

                    await onExport(exportSettings);

                    // Complete the progress
                    clearInterval(progressInterval);
                    setExportProgress(100);

                    // Show completion briefly
                    await new Promise((resolve) => setTimeout(resolve, 300));
                  } catch (error) {
                    console.error("Export failed:", error);
                  } finally {
                    setIsExporting(false);
                    setExportProgress(0);
                  }
                }}
                isDisabled={isExporting}
                className="w-full"
              >
                {isExporting ? (
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">Exporting...</span>
                      <span className="text-xs">{Math.round(exportProgress)}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all duration-200 ease-out"
                        style={{ width: `${exportProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    {`Export (${exportSettings.format.toUpperCase()}, ${exportSettings.quality})`}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Export FAB with Dialog - Only show on mobile (< md breakpoint) */}
      {config.allowExport && onExport && (
        <DialogTrigger>
          <Button
            variant="primary"
            isDisabled={isExporting}
            className="md:hidden fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-xl p-0"
          >
            {isExporting ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Download className="h-6 w-6" />
            )}
          </Button>

          <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Dialog className="rounded-lg bg-background p-6 shadow-lg max-w-md w-full mx-4">
              {({ close }) => (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Export Image</h3>
                    <p className="text-muted-foreground text-sm">
                      Configure export settings and download your edited image.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      label="Format"
                      selectedKey={exportSettings.format}
                      onSelectionChange={(format) =>
                        setExportSettings((prev) => ({ ...prev, format: format as ExportFormat }))
                      }
                    >
                      <SelectItem id="jpeg">JPEG</SelectItem>
                      <SelectItem id="png">PNG</SelectItem>
                      <SelectItem id="webp">WebP</SelectItem>
                    </Select>

                    <NumberField
                      label="Quality"
                      value={exportSettings.quality}
                      minValue={50}
                      maxValue={100}
                      onChange={(quality) =>
                        setExportSettings((prev) => ({ ...prev, quality: quality || 100 }))
                      }
                      placeholder="100"
                    />
                  </div>

                  <div className="flex flex-col gap-2 mt-2">
                    <Button
                      variant="primary"
                      onPress={async () => {
                        setIsExporting(true);
                        setExportProgress(0);

                        try {
                          const progressInterval = setInterval(() => {
                            setExportProgress((prev) => {
                              if (prev >= 90) {
                                clearInterval(progressInterval);
                                return 90;
                              }
                              const increment = Math.random() * 15 + 5;
                              return Math.min(prev + increment, 90);
                            });
                          }, 100);

                          await onExport(exportSettings);

                          clearInterval(progressInterval);
                          setExportProgress(100);
                          await new Promise((resolve) => setTimeout(resolve, 500));

                          // Close dialog after successful export
                          close();
                        } catch (error) {
                          console.error("Export failed:", error);
                        } finally {
                          setIsExporting(false);
                          setExportProgress(0);
                        }
                      }}
                      isDisabled={isExporting}
                      className="w-full"
                    >
                      {isExporting ? (
                        <div className="w-full">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm">Exporting...</span>
                            <span className="text-xs">{Math.round(exportProgress)}%</span>
                          </div>
                          <div className="w-full bg-muted/30 rounded-full h-1.5">
                            <div
                              className="bg-primary-foreground h-1.5 rounded-full transition-all duration-200 ease-out"
                              style={{ width: `${exportProgress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Export Image
                        </>
                      )}
                    </Button>
                    {!isExporting && (
                      <Button variant="outline" onPress={close} className="w-full">
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Dialog>
          </Modal>
        </DialogTrigger>
      )}
    </div>
  );
}
